import {IssueViewer} from '@github-ui/issue-viewer/IssueViewer'
import {IssueViewerContextProvider} from '@github-ui/issue-viewer/IssueViewerContextProvider'
import {IssueViewerLoading} from '@github-ui/issue-viewer/IssueViewerLoading'
import type {OptionConfig} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {noop} from '@github-ui/noop'
import {LinkExternalIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useMemo, useState} from 'react'

import {BROWSER_ANIMATION_DURATION} from '../../../utils/constants'
import type {ExistingIssue} from '../content-preview-types'
import {useContentPreview} from '../ContentPreviewContext'
import filePreviewStyles from '../FilePreview.module.css'
import issuePreviewStyles from '../IssuePreview.module.css'
import {BulkCreateButton} from './BulkCreateButton'
import createIssueStyles from './CreateIssuePreview.module.css'
import styles from './ExistingIssuePreview.module.css'
import {ParentSelector} from './ParentSelector'
import {RelationshipsSection} from './relationships-section/RelationshipsSection'
import {useDraftIssueTreeMap} from './use-draft-issue-tree-map'

export function ExistingIssuePreview({
  isPreviewOpening,
  issue,
  onClose,
}: {
  isPreviewOpening: boolean
  issue: ExistingIssue
  onClose: () => void
}) {
  const [showLoadingState, setShowLoadingState] = useState(isPreviewOpening)

  const {openItem} = useContentPreview()
  const currentNode = useDraftIssueTreeMap().get(issue.tag)!

  // issue viewer causes lag if we try to render it while animating, so we show a loading until the animation is done.
  useEffect(() => {
    if (showLoadingState) {
      const timer = setTimeout(() => setShowLoadingState(false), BROWSER_ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [showLoadingState])

  const openIssueInNewTabButton = useMemo(() => {
    return (
      <IconButton
        icon={LinkExternalIcon}
        variant="invisible"
        aria-label="Open issue in new tab"
        onClick={() => {
          window.open(issue.href, '_blank')
        }}
      />
    )
  }, [issue.href])

  const optionConfig: OptionConfig = useMemo(
    () => ({
      // We don't want the user to manage the issue from here, regardless of permissions. The intent is to
      // provide a preview of the existing issue as part of the draft issue graph. Any edits that happen in
      // this preview would be outside the context of the draft issue flow, causing confusion.
      isReadonly: true,
      // We also hide the timeline, including comments, to eliminate lengthy scrolling between issue body and metadata.
      hideTimeline: true,
      navigate: noop,
      insideSidePanel: true,
      shouldSkipSetDocumentTitle: true,
      useViewportQueries: false,
      onIssueDelete: onClose,
      onRemovedFromProject: noop,
      onIssueUpdate: noop,
      additionalHeaderActions: openIssueInNewTabButton,
      showRepositoryPill: true,
    }),
    [onClose, openIssueInNewTabButton],
  )

  const itemIdentifier: ItemIdentifier | undefined = useMemo(() => {
    const [owner, repo] = issue.repository.split('/')
    if (!owner || !repo) return

    return {
      owner,
      repo,
      number: issue.number,
      type: 'Issue',
    }
  }, [issue.number, issue.repository])

  if (showLoadingState || !itemIdentifier) {
    return <IssueViewerLoading optionConfig={optionConfig} />
  }

  return (
    <div className={clsx(issuePreviewStyles.container, createIssueStyles.container)}>
      <div className={filePreviewStyles.toolbar}>
        <div className={clsx(filePreviewStyles.toolbarLeft, createIssueStyles.toolbarLeft)}>
          {currentNode.parent && (
            <ParentSelector node={currentNode} onItemSelected={node => openItem(node.item.id, true)} />
          )}
        </div>
        <div className={filePreviewStyles.toolbarActions}>
          <BulkCreateButton issueNode={currentNode} onSuccess={onClose} />
        </div>
      </div>
      <div className={clsx(createIssueStyles.viewerContainer, styles.viewerContainer)}>
        <IssueViewerContextProvider>
          <IssueViewer itemIdentifier={itemIdentifier} optionConfig={optionConfig} />
        </IssueViewerContextProvider>
        <div className={styles.relationshipsContainer}>
          <RelationshipsSection issueNode={currentNode} />
        </div>
      </div>
    </div>
  )
}
